/**
 * Component Renderer Core Implementation
 *
 * Main orchestrator that handles form completion events, polls the credit decision API,
 * and renders the appropriate Webflow components based on scenario results.
 */

import type { FormCompletionData } from '../integration'
import type { PollingConfig, PollingResult, CreditDecisionResponse, DataVariables } from '../api'
import type { ComponentSystemConfig, EnvironmentConfig } from '../types'

import { pollCreditDecision } from '../api'
import {
  COMPONENT_CONFIG,
  getComponentActions,
  getEnvironmentConfig,
  detectBrand,
  getProcessingComponentId,
  getOutcomeComponentIds
} from '../config'
import { getGclid, getGcid, cleanSessionId } from '../../utils/tracking-helpers'

/**
 * Global Window interface for GTM dataLayer
 */
declare global {
  interface Window {
    dataLayer: any[];
  }
}

/**
 * Component Renderer Configuration
 */
export interface ComponentRendererOptions {
  /** Environment to use (development/production) */
  environment: 'development' | 'production'
  /** Debug mode override */
  debugMode?: boolean
  /** Custom API headers */
  apiHeaders?: Record<string, string>
  /** Custom polling configuration overrides */
  pollingOverrides?: Partial<PollingConfig>
}

/**
 * Component Rendering Result
 */
export interface ComponentRenderingResult {
  /** Whether rendering was successful */
  success: boolean
  /** Scenario code that was processed */
  scenarioCode?: number
  /** Components that were shown */
  componentsShown: string[]
  /** Components that were hidden */
  componentsHidden: string[]
  /** Whether fallback scenario was used */
  usedFallback: boolean
  /** Total processing time in milliseconds */
  processingTime: number
  /** Error message if unsuccessful */
  error?: string
  /** Credit decision API response */
  creditDecision?: CreditDecisionResponse
  /** API polling result */
  pollingResult?: PollingResult
}

/**
 * Completion State (persisted in localStorage)
 * Stores form completion state for 24 hours to prevent duplicate submissions
 * Includes full credit decision response for instant restoration on refresh
 */
export interface CompletionState {
  /** Session ID from successful form completion */
  sessionId: string
  /** Scenario code that was rendered */
  scenario: number
  /** Timestamp when state was saved */
  timestamp: number
  /** Expiration timestamp (24 hours from save) */
  expiresAt: number
  /** Flag indicating form was completed */
  hasCompletedForm: boolean
  /** Full credit decision response (for instant restoration) */
  creditDecision?: CreditDecisionResponse
  /** Extracted data variables (for instant restoration) */
  dataVariables?: DataVariables
  /** Component actions that were performed */
  componentActions?: {
    show: string[]
    hide: string[]
    useFallback: boolean
  }
}

/**
 * localStorage key for completion state persistence
 */
const COMPLETION_STATE_KEY = 'accs_completion_state'

/**
 * Main Component Renderer Class
 *
 * Handles the complete workflow from form completion to component rendering:
 * 1. Receive form completion event
 * 2. Poll credit decision API
 * 3. Map scenario to component actions
 * 4. Render appropriate components
 */
export class ComponentRenderer {
  private options: ComponentRendererOptions
  private envConfig: EnvironmentConfig
  private debugMode: boolean
  private brand: 'accs' | 'creditdirect'
  private refreshPreventionEnabled: boolean = false
  private scrollLocked: boolean = false
  private savedScrollPosition: number = 0

  constructor(options: ComponentRendererOptions) {
    this.options = options

    // Get environment configuration
    const envConfig = getEnvironmentConfig(options.environment)
    if (!envConfig) {
      throw new Error(`Invalid environment: ${options.environment}`)
    }
    this.envConfig = envConfig

    // Detect brand from hostname
    this.brand = detectBrand()

    // Set debug mode
    this.debugMode = options.debugMode !== undefined ? options.debugMode : envConfig.debugMode

    if (this.debugMode) {
      console.log('🎯 ComponentRenderer initialized', {
        environment: options.environment,
        brand: this.brand,
        debugMode: this.debugMode,
        apiBaseUrl: envConfig.apiBaseUrl
      })
    }
  }

  /**
   * Handle form completion event - main entry point
   */
  async handleFormCompletion(data: FormCompletionData): Promise<ComponentRenderingResult> {
    const startTime = Date.now()

    if (this.debugMode) {
      console.log('🎯 Processing form completion:', data)
    }

    // Fire GTM conversion event immediately on form submission with enhanced conversions
    await this.fireGTMConversionEvent(data.sessionId, 0, data.formData)

    try {
      // Step 0: Show processing component immediately
      await this.showProcessingComponent()

      // Step 1: Check for scenario override from query parameter
      const overrideScenario = this.getScenarioOverride()
      if (overrideScenario !== null) {
        console.log(`🔧 Scenario override detected: ${overrideScenario}`)
        return this.renderOverrideScenario(overrideScenario, startTime, data)
      }

      // Step 2: Call credit decision API
      const pollingResult = await this.pollForCreditDecision(data)

      if (!pollingResult.success || !pollingResult.response) {
        // No credit decision available - use fallback
        return this.renderFallbackComponents(startTime, pollingResult)
      }

      // Step 3: Map scenario to component actions
      const scenarioCode = pollingResult.response.scenario
      const componentActions = getComponentActions(scenarioCode, this.brand)

      if (this.debugMode) {
        console.log('🎯 Scenario mapping:', {
          scenarioCode,
          brand: this.brand,
          actions: componentActions
        })
      }

      // Fire prime_qualified event for scenarios 2 and 4
      if (scenarioCode === 2 || scenarioCode === 4) {
        await this.firePrimeQualifiedEvent(data.sessionId, scenarioCode, data.formData)
      }

      // Fire scenario-specific conversion event (O1, O2, O4, O5, O7, O8, O10)
      await this.fireScenarioConversionEvent(data.sessionId, scenarioCode, data.formData)

      // Step 3: Render components
      const renderingSuccess = await this.renderComponents(
        componentActions.show,
        componentActions.hide
      )

      // Step 4: Inject data variables from API response or DebtSettlementEstimate
      const dataVariables = this.extractDataVariables(pollingResult.response)
      if (dataVariables) {
        this.injectDataVariables(dataVariables)
      }

      // Step 5: Save completion state to localStorage (24 hour persistence)
      // Include full response and data for instant restoration on refresh
      if (renderingSuccess && data.isFormSubmission) {
        this.saveCompletionState(
          data.sessionId,
          scenarioCode,
          pollingResult.response,
          dataVariables,
          componentActions
        )
      }

      // Step 6: Enable refresh prevention after scenario is rendered
      if (renderingSuccess) {
        this.enableRefreshPrevention()
      }

      return {
        success: renderingSuccess,
        scenarioCode,
        componentsShown: componentActions.show,
        componentsHidden: componentActions.hide,
        usedFallback: componentActions.useFallback,
        processingTime: Date.now() - startTime,
        creditDecision: pollingResult.response,
        pollingResult
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown rendering error'

      if (this.debugMode) {
        console.error('🎯 Component rendering failed:', error)
      }

      // On error, render fallback components
      return this.renderFallbackComponents(startTime, undefined, errorMessage)
    }
  }

  /**
   * Get scenario override from URL query parameter
   * Supports: ?scenario=1 through ?scenario=10, and ?scenario=90 through ?scenario=99
   */
  private getScenarioOverride(): number | null {
    try {
      const urlParams = new URLSearchParams(window.location.search)
      const scenarioParam = urlParams.get('scenario')

      if (!scenarioParam) {
        return null
      }

      const scenarioNum = parseInt(scenarioParam, 10)

      // Validate scenario code (1-10, 90-99)
      if (isNaN(scenarioNum)) {
        console.warn(`⚠️ Invalid scenario override: "${scenarioParam}" is not a number`)
        return null
      }

      if (!this.isValidScenario(scenarioNum)) {
        console.warn(`⚠️ Invalid scenario override: ${scenarioNum} is not in valid range (1-10, 90-99)`)
        return null
      }

      return scenarioNum

    } catch (error) {
      console.error('❌ Failed to parse scenario override:', error)
      return null
    }
  }

  /**
   * Get data variable overrides from URL query parameters
   * Supports: ?dr_program_quote=5000&prequal_amt=10000 etc.
   */
  private getDataVariableOverrides(): DataVariables | null {
    try {
      const urlParams = new URLSearchParams(window.location.search)
      const dataVars: DataVariables = {}

      const validKeys: (keyof DataVariables)[] = [
        'dr_program_quote',
        'dr_prequal_debt',
        'prequal_amt',
        'prequal_payment',
        'dr_est_program_length',
        'total_monthly_savings',
        'offer_logo',
        'offer_lender',
        'offer_prequal_amount',
        'offer_prequal_payment',
        'lender_apr',
        'offer_url'
      ]

      let hasAnyVars = false

      for (const key of validKeys) {
        const value = urlParams.get(key)
        if (value !== null) {
          // Format number fields with commas if they're numbers
          if (key === 'dr_prequal_debt' || key === 'offer_prequal_amount') {
            const numValue = parseFloat(value)
            if (!isNaN(numValue)) {
              (dataVars as any)[key] = this.formatNumberWithCommas(numValue)
            } else {
              (dataVars as any)[key] = value
            }
          } else {
            (dataVars as any)[key] = value
          }
          hasAnyVars = true
        }
      }

      if (!hasAnyVars) {
        return null
      }

      if (this.debugMode) {
        console.log('🔧 Data variable overrides detected:', dataVars)
      }

      return dataVars

    } catch (error) {
      console.error('❌ Failed to parse data variable overrides:', error)
      return null
    }
  }

  /**
   * Validate scenario codes
   */
  private isValidScenario(scenario: number): boolean {
    return (scenario >= 1 && scenario <= 10) || (scenario >= 90 && scenario <= 99)
  }

  /**
   * Render components based on override scenario (skip API call)
   */
  private async renderOverrideScenario(
    scenarioCode: number,
    startTime: number,
    data: FormCompletionData
  ): Promise<ComponentRenderingResult> {
    console.log(`🔧 Using scenario override: ${scenarioCode}`)

    // Get data variable overrides from query parameters
    const dataVariables = this.getDataVariableOverrides()

    // Create mock credit decision response
    const mockResponse: CreditDecisionResponse = {
      sessionId: data.sessionId,
      scenario: scenarioCode,
      timestamp: new Date().toISOString(),
      decision: `Override scenario ${scenarioCode}`,
      dataVariables: dataVariables || undefined,
      metadata: {
        externalResponse: { override: true }
      }
    }

    // Map scenario to component actions
    const componentActions = getComponentActions(scenarioCode, this.brand)

    if (this.debugMode) {
      console.log('🎯 Override scenario mapping:', {
        scenarioCode,
        brand: this.brand,
        actions: componentActions,
        dataVariables
      })
    }

    // Render components
    const renderingSuccess = await this.renderComponents(
      componentActions.show,
      componentActions.hide
    )

    // Inject data variables into components
    if (dataVariables) {
      this.injectDataVariables(dataVariables)
    }

    // Fire scenario-specific conversion event for override testing
    await this.fireScenarioConversionEvent(data.sessionId, scenarioCode, data.formData)

    return {
      success: renderingSuccess,
      scenarioCode,
      componentsShown: componentActions.show,
      componentsHidden: componentActions.hide,
      usedFallback: componentActions.useFallback,
      processingTime: Date.now() - startTime,
      creditDecision: mockResponse
    }
  }

  /**
   * Call credit decision API
   */
  private async pollForCreditDecision(data: FormCompletionData): Promise<PollingResult> {
    const pollingConfig: PollingConfig = {
      apiBaseUrl: this.envConfig.apiBaseUrl,
      sessionId: data.sessionId,
      intervalMs: this.envConfig.polling.intervalMs,
      timeoutMs: this.envConfig.polling.timeoutMs,
      maxAttempts: this.envConfig.polling.maxAttempts,
      debugMode: this.debugMode,
      headers: this.options.apiHeaders,
      // Include tracking data for Google Ads
      trackingData: {
        gclid: getGclid(),
        gcid: getGcid(),
        external_transaction_id: cleanSessionId(data.sessionId),
        client_ip_address: undefined // Will be extracted by backend if needed
      },
      ...this.options.pollingOverrides
    }

    const pollingCallbacks = {
      onStart: this.debugMode ? (state: any) => {
        console.log('📞 Credit decision API call started:', state)
      } : undefined,

      onAttempt: this.debugMode ? (state: any, attempt: number) => {
        console.log(`📞 Credit decision API call (attempt ${attempt})`)
      } : undefined,

      onDecision: this.debugMode ? (response: CreditDecisionResponse) => {
        console.log('✅ Credit decision received:', response)
      } : undefined,

      onTimeout: this.debugMode ? (state: any) => {
        console.log('⏰ Credit decision API timed out:', state)
      } : undefined,

      onError: this.debugMode ? (error: string) => {
        console.warn('⚠️ Credit decision API error:', error)
      } : undefined
    }

    return await pollCreditDecision(pollingConfig, pollingCallbacks)
  }

  /**
   * Render fallback components when credit decision is unavailable
   */
  private async renderFallbackComponents(
    startTime: number,
    pollingResult?: PollingResult,
    error?: string
  ): Promise<ComponentRenderingResult> {
    if (this.debugMode) {
      console.log('🎯 Rendering fallback components due to:', error || 'no credit decision')
    }

    const fallbackActions = getComponentActions(-1, this.brand) // Invalid scenario triggers fallback

    const renderingSuccess = await this.renderComponents(
      fallbackActions.show,
      fallbackActions.hide
    )

    return {
      success: renderingSuccess,
      componentsShown: fallbackActions.show,
      componentsHidden: fallbackActions.hide,
      usedFallback: true,
      processingTime: Date.now() - startTime,
      error,
      pollingResult
    }
  }

  /**
   * Show processing component immediately after form submission
   */
  private async showProcessingComponent(): Promise<void> {
    try {
      console.log('⏳ Attempting to show processing component')

      // Get brand-specific component IDs
      const processingComponentId = getProcessingComponentId(this.brand)
      const processingSelector = this.brand === 'creditdirect'
        ? '[data-component="cd-form-processing"]'
        : '[data-component="accs-form-processing"]'

      // Check if processing component exists in DOM
      const processingElements = document.querySelectorAll(processingSelector)
      console.log(`🔍 Found ${processingElements.length} processing component element(s) in DOM`)

      if (processingElements.length === 0) {
        console.error(`❌ Processing component not found in DOM! Check that ${processingSelector} exists in Webflow`)
        return
      }

      // Hide all outcome components for the current brand
      const allOutcomeComponents = getOutcomeComponentIds(this.brand)

      for (const componentId of allOutcomeComponents) {
        await this.hideComponent(componentId)
      }

      // Lock scroll before showing processing component
      this.lockScroll()

      // Show processing component
      await this.showComponent(processingComponentId)

      // Verify it's visible
      const processingEl = processingElements[0] as HTMLElement
      const parentSection = processingEl.closest('.section') as HTMLElement
      console.log('✅ Processing component display:', {
        element: processingEl.style.display,
        parentSection: parentSection?.style.display,
        elementClasses: processingEl.className,
        parentClasses: parentSection?.className
      })

    } catch (error) {
      console.error('❌ Failed to show processing component:', error)
    }
  }

  /**
   * Render components using show/hide strategy
   */
  private async renderComponents(show: string[], hide: string[]): Promise<boolean> {
    try {
      console.log('🎯 [RENDER] Starting renderComponents...', { show, hide })

      // Hide all components first
      console.log('🔍 [RENDER] Hiding components:', hide)
      for (const componentId of hide) {
        console.log(`  ↳ Hiding: ${componentId}`)
        await this.hideComponent(componentId)
      }
      console.log('✅ [RENDER] All components hidden')

      // Then show the required components
      console.log('🔍 [RENDER] Showing components:', show)
      for (const componentId of show) {
        console.log(`  ↳ Showing: ${componentId}`)
        await this.showComponent(componentId)
      }
      console.log('✅ [RENDER] All components shown')

      // Unlock scroll after rendering final outcome
      this.unlockScroll()

      console.log('✅ [RENDER] Component rendering completed successfully')

      return true

    } catch (error) {
      console.error('❌ [RENDER] Component rendering failed:', error)
      console.error('❌ [RENDER] Stack trace:', error)
      // Make sure to unlock scroll even on error
      this.unlockScroll()
      return false
    }
  }

  /**
   * Show a component using CSS display properties
   */
  private async showComponent(componentId: string): Promise<void> {
    const component = COMPONENT_CONFIG.components[componentId]
    if (!component) {
      throw new Error(`Component not found: ${componentId}`)
    }

    const elements = document.querySelectorAll(component.selector)

    if (elements.length === 0) {
      if (this.debugMode) {
        console.warn(`⚠️ No elements found for component: ${componentId} (${component.selector})`)
      }
      return
    }

    elements.forEach((element: Element) => {
      const htmlElement = element as HTMLElement

      // Set display property
      htmlElement.style.display = component.visibleDisplay

      // Add show classes
      component.showClasses?.forEach(cls => htmlElement.classList.add(cls))

      // Remove hide classes
      component.hideClasses?.forEach(cls => htmlElement.classList.remove(cls))

      // CRITICAL: Also show parent section if it's hidden
      const parentSection = htmlElement.closest('.section')
      if (parentSection) {
        const parentEl = parentSection as HTMLElement
        parentEl.style.display = 'block'
        parentEl.classList.remove('component-hidden')
      }
    })

    if (this.debugMode) {
      console.log(`👁️ Showed component: ${componentId} (${elements.length} elements)`)
    }
  }

  /**
   * Hide a component using CSS display properties
   */
  private async hideComponent(componentId: string): Promise<void> {
    const component = COMPONENT_CONFIG.components[componentId]
    if (!component) {
      throw new Error(`Component not found: ${componentId}`)
    }

    const elements = document.querySelectorAll(component.selector)

    if (elements.length === 0) {
      // Not finding elements to hide is not necessarily an error
      if (this.debugMode) {
        console.log(`ℹ️ No elements found to hide for component: ${componentId}`)
      }
      return
    }

    elements.forEach((element: Element) => {
      const htmlElement = element as HTMLElement

      // Set display property
      htmlElement.style.display = component.hiddenDisplay

      // Add hide classes
      component.hideClasses?.forEach(cls => htmlElement.classList.add(cls))

      // Remove show classes
      component.showClasses?.forEach(cls => htmlElement.classList.remove(cls))
    })

    if (this.debugMode) {
      console.log(`🙈 Hid component: ${componentId} (${elements.length} elements)`)
    }
  }

  /**
   * Format number with comma separators (no decimals)
   * Example: 25000 -> "25,000"
   */
  private formatNumberWithCommas(value: number): string {
    return Math.round(value).toLocaleString('en-US', {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    })
  }

  /**
   * Extract data variables from credit decision response
   * Handles DebtSettlementEstimate for DS scenarios (2, 4, 8, 10)
   * Handles EvenOffers for Loan scenarios (1, 2, 8)
   */
  private extractDataVariables(response: CreditDecisionResponse): DataVariables | null {
    const dataVars: DataVariables = {}

    // Check if this scenario includes Debt Settlement data (DS Only: 4, 10; DS+Loan: 2, 8)
    const hasDsData = response.scenario === 2 || response.scenario === 4 || response.scenario === 8 || response.scenario === 10

    // Handle both PascalCase and camelCase for DebtSettlementEstimate
    const debtSettlement = response.DebtSettlementEstimate || (response as any).debtSettlementEstimate
    if (hasDsData && debtSettlement) {
      const estimate = debtSettlement

      // Map DrPreQualDebtAmount to dr_prequal_debt with comma formatting
      if (estimate.DrPreQualDebtAmount !== undefined && estimate.DrPreQualDebtAmount !== null) {
        dataVars.dr_prequal_debt = this.formatNumberWithCommas(estimate.DrPreQualDebtAmount)
      }

      // Map EstimatedPayment to dr_program_quote
      if (estimate.EstimatedPayment !== undefined && estimate.EstimatedPayment !== null) {
        dataVars.dr_program_quote = estimate.EstimatedPayment.toFixed(2)
      }

      // Hardcode DR Est Program Length to 42
      dataVars.dr_est_program_length = '42'

      if (this.debugMode) {
        console.log('💉 Extracted data variables from DebtSettlementEstimate:', dataVars)
      }
    }

    // Check if this scenario includes Loan data (Loan Only: 1; DS+Loan: 2, 8)
    const hasLoanData = response.scenario === 1 || response.scenario === 2 || response.scenario === 8

    // Handle both PascalCase and camelCase for EvenOffers
    const evenOffers = response.EvenOffers || (response as any).evenOffers
    if (hasLoanData && evenOffers && evenOffers.loanOffers && evenOffers.loanOffers.length > 0) {
      const firstLoanOffer = evenOffers.loanOffers[0]

      // Extract offer_logo from originator.images[0].url
      if (firstLoanOffer.originator?.images && firstLoanOffer.originator.images.length > 0) {
        dataVars.offer_logo = firstLoanOffer.originator.images[0].url
      }

      // Extract offer_lender from originator.name
      if (firstLoanOffer.originator?.name) {
        dataVars.offer_lender = firstLoanOffer.originator.name
      }

      // Extract offer_prequal_amount from maxAmount (with comma formatting)
      if (firstLoanOffer.maxAmount !== undefined && firstLoanOffer.maxAmount !== null) {
        dataVars.offer_prequal_amount = this.formatNumberWithCommas(firstLoanOffer.maxAmount)
      }

      // Extract offer_prequal_payment from monthlyPayment (with decimal formatting)
      if (firstLoanOffer.monthlyPayment !== undefined && firstLoanOffer.monthlyPayment !== null) {
        dataVars.offer_prequal_payment = firstLoanOffer.monthlyPayment.toFixed(2)
      }

      // Extract lender_apr from maxApr
      if (firstLoanOffer.maxApr) {
        dataVars.lender_apr = firstLoanOffer.maxApr
      }

      // Extract offer_url from url
      if (firstLoanOffer.url) {
        dataVars.offer_url = firstLoanOffer.url
      }

      if (this.debugMode) {
        console.log('💉 Extracted data variables from EvenOffers:', dataVars)
      }
    }

    // Also include any data variables from the API response
    if (response.dataVariables) {
      Object.assign(dataVars, response.dataVariables)
    }

    // Return null if no data variables
    return Object.keys(dataVars).length > 0 ? dataVars : null
  }

  /**
   * Inject data variables into DOM elements with data-variable attributes
   */
  private injectDataVariables(dataVariables: DataVariables): void {
    if (this.debugMode) {
      console.log('💉 Injecting data variables into components:', dataVariables)
    }

    let injectedCount = 0

    // Iterate through each data variable
    for (const [key, value] of Object.entries(dataVariables)) {
      if (value === undefined) continue

      // Find all elements with matching data-variable attribute
      const selector = `[data-variable="${key}"]`
      const elements = document.querySelectorAll(selector)

      if (elements.length === 0) {
        if (this.debugMode) {
          console.log(`ℹ️ No elements found for data-variable="${key}"`)
        }
        continue
      }

      // Inject value into each matching element
      elements.forEach((element: Element) => {
        const htmlElement = element as HTMLElement

        // Special handling for offer_logo (image URLs)
        if (key === 'offer_logo' && htmlElement.tagName === 'IMG') {
          (htmlElement as HTMLImageElement).src = String(value)
          htmlElement.setAttribute('alt', 'Lender Logo')
        }
        // Special handling for offer_url (button/link URLs)
        else if (key === 'offer_url' && (htmlElement.tagName === 'A' || htmlElement.tagName === 'BUTTON')) {
          if (htmlElement.tagName === 'A') {
            (htmlElement as HTMLAnchorElement).href = String(value)
          } else if (htmlElement.tagName === 'BUTTON') {
            // For buttons, set onclick to navigate
            htmlElement.setAttribute('onclick', `window.location.href='${String(value)}'`)
          }
        }
        else {
          // Set text content for non-special elements
          htmlElement.textContent = String(value)
        }

        // Also set as data attribute for reference
        htmlElement.setAttribute('data-value', String(value))

        injectedCount++
      })

      if (this.debugMode) {
        console.log(`💉 Injected "${key}" = "${value}" into ${elements.length} element(s)`)
      }
    }

    if (this.debugMode) {
      console.log(`✅ Data variable injection complete: ${injectedCount} total injections`)
    }
  }

  /**
   * Hash a string using SHA-256 for Google Ads Enhanced Conversions
   */
  private async hashSHA256(value: string | undefined): Promise<string | undefined> {
    if (!value || value.trim().length === 0) {
      return undefined
    }

    try {
      // Normalize: lowercase and trim whitespace
      const normalized = value.toLowerCase().trim()

      // Convert string to ArrayBuffer
      const encoder = new TextEncoder()
      const data = encoder.encode(normalized)

      // Hash using SubtleCrypto (browser native)
      const hashBuffer = await crypto.subtle.digest('SHA-256', data)

      // Convert ArrayBuffer to hex string
      const hashArray = Array.from(new Uint8Array(hashBuffer))
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')

      return hashHex
    } catch (error) {
      if (this.debugMode) {
        console.warn('⚠️ Failed to hash value:', error)
      }
      return undefined
    }
  }

  /**
   * Fire GTM conversion event via dataLayer with enhanced conversions data
   */
  private async fireGTMConversionEvent(
    sessionId: string,
    scenarioCode: number,
    formData?: FormCompletionData['formData']
  ): Promise<void> {
    try {
      // Ensure dataLayer exists
      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || []

        // Prepare enhanced conversions data if form data is available
        const enhancedConversionData: Record<string, any> = {}

        if (formData) {
          // Hash PII fields for Google Ads Enhanced Conversions
          // According to Google's spec, these should be SHA-256 hashed and normalized
          const [hashedEmail, hashedPhone, hashedFirstName, hashedLastName, hashedStreet] = await Promise.all([
            this.hashSHA256(formData.email),
            this.hashSHA256(formData.phone),
            this.hashSHA256(formData.first_name),
            this.hashSHA256(formData.last_name),
            this.hashSHA256(formData.address_street)
          ])

          // Build enhanced conversion data object with hashed values
          // Google's format: field names WITHOUT "sha256_" prefix - hashing is implied
          if (hashedEmail) enhancedConversionData.email = hashedEmail
          if (hashedPhone) enhancedConversionData.phone_number = hashedPhone
          if (hashedFirstName) enhancedConversionData.first_name = hashedFirstName
          if (hashedLastName) enhancedConversionData.last_name = hashedLastName

          // Address fields (only include street - Google's enhanced conversion format)
          if (hashedStreet) {
            enhancedConversionData.address = {
              street: hashedStreet
            }
          }
        }

        // Push conversion event to dataLayer with enhanced conversions
        // Strip "sess_" prefix from session ID for cleaner tracking
        const cleanSessionId = sessionId.replace(/^sess_/, '')

        const eventData: Record<string, any> = {
          event: 'conversion',
          session_id: cleanSessionId
        }

        // Add enhanced conversion data if available
        if (Object.keys(enhancedConversionData).length > 0) {
          eventData.enhanced_conversion_data = enhancedConversionData
        }

        window.dataLayer.push(eventData)

        if (this.debugMode) {
          console.log('📊 GTM Conversion Event Pushed to dataLayer:', {
            event: 'conversion',
            session_id: cleanSessionId,
            scenario_code: scenarioCode,
            has_enhanced_data: Object.keys(enhancedConversionData).length > 0,
            enhanced_fields: Object.keys(enhancedConversionData)
          })
        }
      }
    } catch (error) {
      console.error('❌ Failed to push GTM conversion event:', error)
    }
  }

  /**
   * Fire prime_qualified event via dataLayer for scenarios 2 and 4
   * Includes enhanced conversions data like the main conversion event
   */
  private async firePrimeQualifiedEvent(
    sessionId: string,
    scenarioCode: number,
    formData?: FormCompletionData['formData']
  ): Promise<void> {
    try {
      // Ensure dataLayer exists
      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || []

        // Prepare enhanced conversions data if form data is available
        const enhancedConversionData: Record<string, any> = {}

        if (formData) {
          // Hash PII fields for Google Ads Enhanced Conversions
          const [hashedEmail, hashedPhone, hashedFirstName, hashedLastName, hashedStreet] = await Promise.all([
            this.hashSHA256(formData.email),
            this.hashSHA256(formData.phone),
            this.hashSHA256(formData.first_name),
            this.hashSHA256(formData.last_name),
            this.hashSHA256(formData.address_street)
          ])

          // Build enhanced conversion data object with hashed values
          if (hashedEmail) enhancedConversionData.email = hashedEmail
          if (hashedPhone) enhancedConversionData.phone_number = hashedPhone
          if (hashedFirstName) enhancedConversionData.first_name = hashedFirstName
          if (hashedLastName) enhancedConversionData.last_name = hashedLastName

          // Address fields (only include street)
          if (hashedStreet) {
            enhancedConversionData.address = {
              street: hashedStreet
            }
          }
        }

        // Push prime_qualified event to dataLayer
        const cleanSessionId = sessionId.replace(/^sess_/, '')

        const eventData: Record<string, any> = {
          event: 'prime_qualified',
          session_id: cleanSessionId,
          scenario_code: scenarioCode
        }

        // Add enhanced conversion data if available
        if (Object.keys(enhancedConversionData).length > 0) {
          eventData.enhanced_conversion_data = enhancedConversionData
        }

        window.dataLayer.push(eventData)

        if (this.debugMode) {
          console.log('🎯 Prime Qualified Event Pushed to dataLayer:', {
            event: 'prime_qualified',
            session_id: cleanSessionId,
            scenario_code: scenarioCode,
            has_enhanced_data: Object.keys(enhancedConversionData).length > 0,
            enhanced_fields: Object.keys(enhancedConversionData)
          })
        }
      }
    } catch (error) {
      console.error('❌ Failed to push prime_qualified event:', error)
    }
  }

  /**
   * Fire scenario-specific conversion event via dataLayer
   * Maps scenario codes to conversion names (O1, O2, O4, O5, O7, O8, O10)
   * Includes enhanced conversions data for Google Ads
   */
  private async fireScenarioConversionEvent(
    sessionId: string,
    scenarioCode: number,
    formData?: FormCompletionData['formData']
  ): Promise<void> {
    try {
      // Map scenario codes to conversion names (brand-specific)
      const accsConversionMap: Record<number, string> = {
        1: 'O1',   // Loan Only
        2: 'O2',   // DS + Loan (Prime)
        4: 'O4',   // DS Only (Prime)
        5: 'O5',   // Unqualified
        7: 'O7',   // Unqualified (Alternative)
        8: 'O8',   // DS + Loan Combo
        10: 'O10'  // DS Only
        // Note: Scenarios 3, 6, 9 are Fallback - no conversion events
      }

      const creditDirectConversionMap: Record<number, string> = {
        1: 'O1',   // Partner Only
        2: 'O2',   // DS + Loan (Prime)
        3: 'O3',   // App Process Error
        4: 'O4',   // DS Only (Prime)
        5: 'O5',   // Unqualified
        6: 'O6',   // Loan Only (Direct)
        7: 'O7',   // Unqualified (Alternative)
        8: 'O8',   // DS + Loan Combo
        9: 'O9',   // App Process Error (Alternative)
        10: 'O10'  // DS Only
      }

      // Select the appropriate conversion map based on brand
      const scenarioConversionMap = this.brand === 'creditdirect'
        ? creditDirectConversionMap
        : accsConversionMap

      // Check if this scenario has a conversion event configured
      const conversionName = scenarioConversionMap[scenarioCode]
      if (!conversionName) {
        if (this.debugMode) {
          console.log(`ℹ️ No conversion event configured for scenario ${scenarioCode}`)
        }
        return
      }

      // Ensure dataLayer exists
      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || []

        // Prepare enhanced conversions data if form data is available
        const enhancedConversionData: Record<string, any> = {}

        if (formData) {
          // Hash PII fields for Google Ads Enhanced Conversions
          const [hashedEmail, hashedPhone, hashedFirstName, hashedLastName, hashedStreet] = await Promise.all([
            this.hashSHA256(formData.email),
            this.hashSHA256(formData.phone),
            this.hashSHA256(formData.first_name),
            this.hashSHA256(formData.last_name),
            this.hashSHA256(formData.address_street)
          ])

          // Build enhanced conversion data object with hashed values
          if (hashedEmail) enhancedConversionData.email = hashedEmail
          if (hashedPhone) enhancedConversionData.phone_number = hashedPhone
          if (hashedFirstName) enhancedConversionData.first_name = hashedFirstName
          if (hashedLastName) enhancedConversionData.last_name = hashedLastName

          // Address fields (only include street)
          if (hashedStreet) {
            enhancedConversionData.address = {
              street: hashedStreet
            }
          }
        }

        // Push scenario conversion event to dataLayer
        const cleanSessionId = sessionId.replace(/^sess_/, '')

        const eventData: Record<string, any> = {
          event: conversionName,
          session_id: cleanSessionId,
          scenario_code: scenarioCode
        }

        // Add enhanced conversion data if available
        if (Object.keys(enhancedConversionData).length > 0) {
          eventData.enhanced_conversion_data = enhancedConversionData
        }

        window.dataLayer.push(eventData)

        if (this.debugMode) {
          console.log(`🎯 Scenario Conversion Event "${conversionName}" Pushed to dataLayer:`, {
            event: conversionName,
            session_id: cleanSessionId,
            scenario_code: scenarioCode,
            has_enhanced_data: Object.keys(enhancedConversionData).length > 0,
            enhanced_fields: Object.keys(enhancedConversionData)
          })
        }
      }
    } catch (error) {
      console.error('❌ Failed to push scenario conversion event:', error)
    }
  }

  /**
   * Save completion state to localStorage
   * Persists for 24 hours to prevent duplicate form submissions on page refresh
   */
  private saveCompletionState(
    sessionId: string,
    scenario: number,
    creditDecision?: CreditDecisionResponse,
    dataVariables?: DataVariables | null,
    componentActions?: { show: string[]; hide: string[]; useFallback: boolean }
  ): void {
    try {
      const state: CompletionState = {
        sessionId,
        scenario,
        timestamp: Date.now(),
        expiresAt: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
        hasCompletedForm: true,
        creditDecision,
        dataVariables: dataVariables || undefined,
        componentActions
      }

      localStorage.setItem(COMPLETION_STATE_KEY, JSON.stringify(state))

      if (this.debugMode) {
        console.log('💾 Saved completion state to localStorage:', {
          sessionId,
          scenario,
          expiresAt: new Date(state.expiresAt).toISOString(),
          hasCreditDecision: !!creditDecision,
          hasDataVariables: !!dataVariables,
          hasComponentActions: !!componentActions
        })
      }
    } catch (error) {
      console.error('❌ Failed to save completion state:', error)
    }
  }

  /**
   * Load completion state from localStorage
   * Returns null if not found, expired, or invalid
   */
  private loadCompletionState(): CompletionState | null {
    try {
      const saved = localStorage.getItem(COMPLETION_STATE_KEY)
      if (!saved) {
        return null
      }

      const state: CompletionState = JSON.parse(saved)

      // Validate structure
      if (!state.sessionId || !state.expiresAt || typeof state.scenario !== 'number') {
        if (this.debugMode) {
          console.warn('⚠️ Invalid completion state structure, clearing...')
        }
        this.clearCompletionState()
        return null
      }

      // Check if expired (24 hours)
      if (Date.now() > state.expiresAt) {
        if (this.debugMode) {
          console.log('⏰ Completion state expired, clearing...')
        }
        this.clearCompletionState()
        return null
      }

      if (this.debugMode) {
        const hoursRemaining = ((state.expiresAt - Date.now()) / (1000 * 60 * 60)).toFixed(1)
        console.log('📂 Loaded completion state from localStorage:', {
          sessionId: state.sessionId,
          scenario: state.scenario,
          hoursRemaining
        })
      }

      return state
    } catch (error) {
      console.error('❌ Failed to load completion state:', error)
      this.clearCompletionState()
      return null
    }
  }

  /**
   * Clear completion state from localStorage
   * Made public so lead tracker can clear state on failed restoration
   */
  clearCompletionState(): void {
    try {
      localStorage.removeItem(COMPLETION_STATE_KEY)
      if (this.debugMode) {
        console.log('🗑️ Cleared completion state from localStorage')
      }
    } catch (error) {
      console.error('❌ Failed to clear completion state:', error)
    }
  }

  /**
   * Check for saved completion state (public method for lead tracker)
   * Returns saved state if found and valid (< 24 hours old)
   */
  checkForSavedState(): CompletionState | null {
    return this.loadCompletionState()
  }

  /**
   * Restore saved component state from localStorage
   * Used on page load when user returns within 24 hours
   */
  async restoreSavedState(savedState: CompletionState): Promise<ComponentRenderingResult> {
    const startTime = Date.now()

    console.log('🔄 [COMPONENT RENDERER] Starting restoreSavedState...', savedState)

    try {
      // Check if we have saved credit decision data (instant restoration)
      const hasSavedData = savedState.creditDecision && savedState.componentActions
      console.log('🔍 [COMPONENT RENDERER] Has saved data?', hasSavedData)

      if (hasSavedData) {
        console.log('⚡ [COMPONENT RENDERER] Using saved credit decision data - instant restoration (no API call)')

        // Get component actions from saved state
        const componentActions = savedState.componentActions!
        const scenarioCode = savedState.scenario

        console.log('🔍 [COMPONENT RENDERER] Component actions:', {
          show: componentActions.show,
          hide: componentActions.hide,
          scenarioCode: scenarioCode
        })

        // Render components directly from saved state
        console.log('🔍 [COMPONENT RENDERER] Calling renderComponents...')
        const renderingSuccess = await this.renderComponents(
          componentActions.show,
          componentActions.hide
        )
        console.log('✅ [COMPONENT RENDERER] renderComponents returned:', renderingSuccess)

        // Inject saved data variables
        if (savedState.dataVariables) {
          this.injectDataVariables(savedState.dataVariables)
        }

        // Fire scenario-specific conversion event (restored state)
        // Note: formData not available on restore, but event still fires without enhanced conversions
        await this.fireScenarioConversionEvent(savedState.sessionId, scenarioCode)

        // Enable refresh prevention
        if (renderingSuccess) {
          this.enableRefreshPrevention()
        }

        return {
          success: renderingSuccess,
          scenarioCode,
          componentsShown: componentActions.show,
          componentsHidden: componentActions.hide,
          usedFallback: componentActions.useFallback,
          processingTime: Date.now() - startTime,
          creditDecision: savedState.creditDecision
        }
      }

      // Fallback: No saved data - call API (backward compatibility)
      if (this.debugMode) {
        console.log('⚠️ No saved data found - falling back to API call')
      }

      // Show processing component
      await this.showProcessingComponent()

      // Create form completion data with saved session ID
      const formData: FormCompletionData = {
        sessionId: savedState.sessionId,
        submissionTime: savedState.timestamp,
        stepNumber: 0,
        isFormSubmission: false,
        debugMode: this.debugMode
      }

      // Call credit decision API with saved session ID
      const pollingResult = await this.pollForCreditDecision(formData)

      if (!pollingResult.success || !pollingResult.response) {
        // API failed - clear saved state and render fallback
        console.warn('⚠️ API failed on restore, clearing saved state')
        this.clearCompletionState()
        return this.renderFallbackComponents(startTime, pollingResult)
      }

      // Get scenario from API response (could be original or scenario 90 if already applied)
      const scenarioCode = pollingResult.response.scenario
      const componentActions = getComponentActions(scenarioCode, this.brand)

      if (this.debugMode) {
        console.log('🎯 Restoring scenario:', {
          savedScenario: savedState.scenario,
          apiScenario: scenarioCode,
          brand: this.brand,
          actions: componentActions
        })
      }

      // Render components
      const renderingSuccess = await this.renderComponents(
        componentActions.show,
        componentActions.hide
      )

      // Inject data variables from API response
      const dataVariables = this.extractDataVariables(pollingResult.response)
      if (dataVariables) {
        this.injectDataVariables(dataVariables)
      }

      // Fire scenario-specific conversion event (API fallback path)
      // Note: formData not available on restore, but event still fires without enhanced conversions
      await this.fireScenarioConversionEvent(savedState.sessionId, scenarioCode)

      // Enable refresh prevention for restored state
      if (renderingSuccess) {
        this.enableRefreshPrevention()
      }

      // If scenario changed (e.g., now returns scenario 90), update saved state
      if (scenarioCode !== savedState.scenario) {
        if (this.debugMode) {
          console.log('📝 Scenario changed, updating saved state')
        }
        this.saveCompletionState(
          savedState.sessionId,
          scenarioCode,
          pollingResult.response,
          dataVariables,
          componentActions
        )
      }

      return {
        success: renderingSuccess,
        scenarioCode,
        componentsShown: componentActions.show,
        componentsHidden: componentActions.hide,
        usedFallback: componentActions.useFallback,
        processingTime: Date.now() - startTime,
        creditDecision: pollingResult.response,
        pollingResult
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown restore error'

      if (this.debugMode) {
        console.error('🎯 Component restore failed:', error)
      }

      // Clear saved state on error
      this.clearCompletionState()

      // Render fallback components
      return this.renderFallbackComponents(startTime, undefined, errorMessage)
    }
  }

  /**
   * Enable browser refresh prevention
   * Shows warning when user tries to refresh/leave page after scenario is rendered
   */
  private enableRefreshPrevention(): void {
    // Only set up once
    if (this.refreshPreventionEnabled) {
      return
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Standard message for modern browsers
      const message = 'Your credit decision results are displayed. Refreshing the page will not change your results.'

      // For older browsers
      event.returnValue = message

      // For modern browsers
      return message
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    this.refreshPreventionEnabled = true

    if (this.debugMode) {
      console.log('🛡️ Browser refresh prevention enabled')
    }
  }

  /**
   * Lock page scrolling
   * Prevents user from scrolling while processing component is visible
   */
  private lockScroll(): void {
    if (this.scrollLocked) {
      return
    }

    // Save current scroll position
    this.savedScrollPosition = window.pageYOffset || document.documentElement.scrollTop

    // Add CSS to prevent scrolling
    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.top = `-${this.savedScrollPosition}px`
    document.body.style.width = '100%'

    this.scrollLocked = true

    if (this.debugMode) {
      console.log('🔒 Page scroll locked at position:', this.savedScrollPosition)
    }
  }

  /**
   * Unlock page scrolling
   * Restores normal scrolling after final component is rendered
   */
  private unlockScroll(): void {
    if (!this.scrollLocked) {
      return
    }

    // Remove scroll-prevention CSS
    document.body.style.overflow = ''
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.width = ''

    // Restore scroll position
    window.scrollTo(0, this.savedScrollPosition)

    this.scrollLocked = false

    if (this.debugMode) {
      console.log('🔓 Page scroll unlocked, restored position:', this.savedScrollPosition)
    }
  }

  /**
   * Get current renderer status
   */
  getStatus() {
    return {
      environment: this.options.environment,
      debugMode: this.debugMode,
      apiBaseUrl: this.envConfig.apiBaseUrl,
      pollingConfig: this.envConfig.polling,
      refreshPreventionEnabled: this.refreshPreventionEnabled
    }
  }
}